import { FC, MouseEvent, useState } from "react";
import { useSelector } from "react-redux";
import { AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";

import { ProductionNoteResource } from "@/types/resources";
import { ProductionProjectNoteStatusEnum } from "@/utils/enums";
import Text from "@/components/Text";
import Button from "@/components/Button";
import NotesModal from "./NotesModal";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { RootState } from "@/utils/store";

const NoteBox = styled.div`
  padding: 24px;
  padding-bottom: 0;
`;

const NoteHeading = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledText = styled(Text)`
  line-height: 22px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const StyledButton = styled(Button)`
  font-size: 11px;
  height: 27px;
`;

const StatusBadge = styled(Text)<{ $status: ProductionProjectNoteStatusEnum }>`
  border-radius: 4px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  ${({ theme, $status }) =>
    $status === ProductionProjectNoteStatusEnum.CANCELED &&
    css`
      background: ${theme.colors.errorLight};
    `}
  ${({ theme, $status }) =>
    $status === ProductionProjectNoteStatusEnum.INFO &&
    css`
      background: ${theme.colors.productionInfoLight};
    `}
    ${({ theme, $status }) =>
    $status === ProductionProjectNoteStatusEnum.WARNING &&
    css`
      background: ${theme.colors.warningLight};
    `}
`;

const NoteButtons = styled.div`
  display: flex;
  gap: 8px;
`;

interface Props {
  note: ProductionNoteResource;
  index: number;
}

const NoteItem: FC<Props> = ({ note, index }) => {
  const [showNotesModal, setShowNotesModal] = useState(false);

  const { projectId } = useParams();

  const currentUserId = useSelector(
    (rootState: RootState) => rootState.auth.user!.id
  );

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const renderStatus = () => {
    switch (note.status) {
      case ProductionProjectNoteStatusEnum.CANCELED:
        return "Canceled";
      case ProductionProjectNoteStatusEnum.WARNING:
        return "Warning";
      case ProductionProjectNoteStatusEnum.INFO:
        return "Info";
    }
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this note?"
      );

      if (confirm) {
        await api.delete<never, { id: number }>(
          `/v1/production/projects/${projectId}/notes/${note.id}`
        );
        alerts.success("Note successfully deleted");

        queryClient.invalidateQueries(["production_projects", { projectId }]);
      }
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <>
      <NoteBox>
        <NoteHeading>
          <Text $type="smallHeadline">Note {index + 1}</Text>
          <NoteButtons>
            <StatusBadge $status={note.status} $type="smallHeadline">
              {renderStatus()}
            </StatusBadge>
            {currentUserId === note.userId && (
              <StyledButton
                onClick={() => setShowNotesModal(true)}
                small
                color="action"
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 11 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.78936 10H1.39848C1.2928 10 1.19144 9.95802 1.11671 9.88329C1.04198 9.80856 1 9.7072 1 9.60152V7.37569C1 7.32336 1.01031 7.27155 1.03033 7.2232C1.05036 7.17485 1.07971 7.13092 1.11671 7.09392L7.09392 1.11671C7.16865 1.04198 7.27001 1 7.37569 1C7.48137 1 7.58273 1.04198 7.65746 1.11671L9.88329 3.34254C9.95802 3.41727 10 3.51863 10 3.62431C10 3.72999 9.95802 3.83135 9.88329 3.90608L3.78936 10Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 3L8 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Edit
              </StyledButton>
            )}

            <StyledButton small color="action" onClick={onDelete}>
              <svg
                width="11"
                height="13"
                viewBox="0 0 11 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.0379 2.92871L0.962891 2.92872"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.2627 5.5V8.92857"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.73828 5.5V8.92857"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.21309 2.92871V11.0716C9.21309 11.1852 9.16963 11.2942 9.09227 11.3746C9.01491 11.455 8.90999 11.5001 8.80059 11.5001H2.20059C2.09118 11.5001 1.98626 11.455 1.9089 11.3746C1.83155 11.2942 1.78809 11.1852 1.78809 11.0716V2.92871"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.5625 2.92913V2.07199C7.5625 1.84466 7.47558 1.62664 7.32086 1.4659C7.16615 1.30515 6.9563 1.21484 6.7375 1.21484H4.2625C4.0437 1.21484 3.83385 1.30515 3.67914 1.4659C3.52442 1.62664 3.4375 1.84466 3.4375 2.07199V2.92913"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Delete note
            </StyledButton>
          </NoteButtons>
        </NoteHeading>
        <StyledText $type="captionSemibold">{note.content}</StyledText>
      </NoteBox>
      <AnimatePresence>
        {showNotesModal && (
          <NotesModal note={note} onClose={() => setShowNotesModal(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default NoteItem;
